import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { Navigation, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { merge } from 'lodash';
import { from } from 'rxjs';
import { VendorInviteForm } from 'src/app/classes/form-groups/vendor-invite-form';
import {
  Company,
  CompanyComplianceType,
  CompanyVendor,
  UserAccountRole
} from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { Vendor } from 'src/app/interfaces/models/vendor';
import { AddClientRegisteredVendorModalComponent } from 'src/app/modals/add-client-registered-vendor-modal/add-client-registered-vendor-modal.component';
import { SelectComplianceProgramModalComponent } from 'src/app/modals/select-compliance-program-modal/select-compliance-program-modal.component';
import { VendorInviteModalComponent } from 'src/app/modals/vendor-invite-modal/vendor-invite-modal.component';
import { VideoPlayerModalComponent } from 'src/app/modals/video-player-modal/video-player-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorInviteService } from 'src/app/services/vendor-invite.service';

@Component({
  selector: 'app-client-my-vendors',
  templateUrl: './client-my-vendors.component.html',
  styleUrls: ['./client-my-vendors.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorsComponent implements OnInit, OnChanges {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private Router: Router,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private VendorInviteService: VendorInviteService,
    private AuthService: AuthService,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService
  ) {}

  @Input() CompanyId!: number;
  @Input() currentNavigationData?: Navigation | null;
  @Input() AdditionalQueryData?: GenericObject;
  @Input() OriginalQueryData?: GenericObject;
  @Input() Filters: Map<string, any> = new Map();

  @Output() dataGridQueryChanged = new EventEmitter<{
    GridQuery: GenericObject;
    GridRequest: IServerSideGetRowsRequest;
  }>();

  // Parent (client-vendors) gates "Add new vendor" on Company flags via
  // ViewChild; emit so the zoneless parent re-renders when flags arrive.
  @Output() companyLoaded = new EventEmitter<Company>();

  VendorId!: number;
  Vendor!: Vendor;
  Company!: Company;
  SubNationalDivisions?: SubNationalDivision[];
  title: string = 'My Vendors';
  refreshGrid: boolean = false;

  routerNavigationArray: string[] = [];

  suppliedGridQueryData?: GenericObject;

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view']
  };

  UserAccountRole?: UserAccountRole;

  DefaultFilterModel: FilterModel = {
    viveStatus: {
      filterType: 'set',
      values: [
        'Approved',
        'Awaiting Payment',
        'Business Verified',
        'Compliance Exempt',
        'Non-Verified',
        'Registered',
        'Registering',
        'Renewing',
        'Reviewing',
        'Site Conditional',
        'Suspended'
      ]
    }
  };

  @Input() FilterModel: FilterModel = {};

  addVendorTutorialVideo() {
    const { componentInstance } = this.ModalService.open(
      VideoPlayerModalComponent,
      { backdrop: 'static', size: 'xl' }
    );

    Object.assign(componentInstance, {
      IFrameOptions: {
        src: 'https://player.vimeo.com/video/1078744315?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        style: 'width:100%;height:100%;',
        title: 'NONVerified2'
      }
    });
  }

  navigateToAddClientRegisteredVendor() {
    this.Router.navigate([
      'client',
      this.CompanyId,
      'vendors',
      'client-registered-vendor',
      'add'
    ]);
  }

  navigateToAddExemptVendor() {
    this.Router.navigate([
      'client',
      this.CompanyId,
      'vendors',
      'exempt-vendor',
      'add'
    ]);
  }

  addVendor() {
    const allowClientRegisteredVendors =
      !!this.Company?.allowClientRegisteredVendors;
    const allowExemptVendors = !!this.Company?.allowExemptVendors;

    if (!allowClientRegisteredVendors && !allowExemptVendors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'This client is not configured to add Non-Verified, Business Verified, or Compliance Exempt vendors.',
        { returnError: true }
      );
    }

    // Skip the chooser when only one profile type is enabled.
    if (allowClientRegisteredVendors && !allowExemptVendors) {
      this.navigateToAddClientRegisteredVendor();
      return;
    }

    if (!allowClientRegisteredVendors && allowExemptVendors) {
      this.navigateToAddExemptVendor();
      return;
    }

    const clientRegisteredOrExemptVendorConfirmModal = this.ModalService.open(
      AddClientRegisteredVendorModalComponent,
      { backdrop: 'static', centered: true, size: 'lg' }
    );
    clientRegisteredOrExemptVendorConfirmModal.closed.subscribe({
      next: (result: 'addClientRegisteredVendor' | 'addExemptVendor') => {
        if (!result) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot Add Vendor. No Selection Found.',
            { returnError: true }
          );
        }

        if (result === 'addClientRegisteredVendor') {
          this.navigateToAddClientRegisteredVendor();
        }

        if (result === 'addExemptVendor') {
          this.navigateToAddExemptVendor();
        }
      },
      error: (err) => {
        throw this.ToastService.addErrorToast('Error', err.message, {
          returnError: true
        });
      }
    });
  }

  inviteVendor() {
    const { componentInstance, result } = this.ModalService.open(
      SelectComplianceProgramModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    Object.assign(componentInstance, { CompanyId: this.CompanyId });

    from(result).subscribe({
      next: (complianceTypeId: CompanyComplianceType) => {
        if (complianceTypeId) {
          this.sendInvite(complianceTypeId);
        }
      }
    });
  }

  sendInvite(CompanyComplianceTypeId: CompanyComplianceType | null) {
    const { componentInstance, result } = this.ModalService.open(
      VendorInviteModalComponent,
      { backdrop: 'static', size: 'md' }
    );

    componentInstance.CompanyId = this.CompanyId;

    from(result).subscribe({
      next: (VendorInviteFormGroup: VendorInviteForm) => {
        const { value: VendorInvite } = VendorInviteFormGroup;

        Object.assign(VendorInvite, {
          CompanyComplianceTypeId: CompanyComplianceTypeId
        });

        this.VendorInviteService.saveVendorInvite(VendorInvite).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Vendor Invite sent successfully!'
            );
          }
        });
      }
    });
  }

  viewVendor(rowData: any) {
    if (!rowData) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No CompanyVendor data found in row',
        { returnError: true }
      );
    }

    const routerNavigationArray: (string | number)[] = [
      'client',
      this.CompanyId,
      'vendors'
    ];
    const ExemptVendorId = rowData['ExemptVendorId'] || null;
    const VendorId = rowData['VendorId'] || null;
    const ClientRegisteredVendorId =
      rowData['ClientRegisteredVendorId'] || null;

    if (VendorId) {
      routerNavigationArray.push('my-vendor', VendorId, 'info');
    }

    if (ExemptVendorId) {
      routerNavigationArray.push(
        'my-vendor',
        'exempt-vendor',
        ExemptVendorId,
        'info'
      );
    }

    if (ClientRegisteredVendorId) {
      routerNavigationArray.push(
        'my-vendor',
        'client-registered-vendor',
        ClientRegisteredVendorId,
        'info'
      );
    }

    this.Router.navigate(routerNavigationArray);
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'view') {
      this.viewVendor(rowData as CompanyVendor);
    }
  }

  onResetFilters(event: boolean) {
    if (event) {
      if (this.OriginalQueryData) {
        this.Filters = new Map();
        this.suppliedGridQueryData = this.OriginalQueryData;
        this._cdr.markForCheck();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { AdditionalQueryData } = changes;

    if (AdditionalQueryData) {
      const { currentValue } = AdditionalQueryData;

      // Should Additional query data be merged into suppliedQueryData?
      this.suppliedGridQueryData = currentValue;
      this._cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    const user = this.AuthService.getUser();
    this.UserAccountRole = user.UserAccountRole;

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (this.FilterModel) {
      this.FilterModel = Object.assign(
        this.DefaultFilterModel,
        this.FilterModel
      );
    }

    if (this.AdditionalQueryData) {
      merge(this.suppliedGridQueryData, this.AdditionalQueryData);
    }

    this.CompanyService.getCompany(
      this.CompanyId,
      this.UtilityService.stringifyObjectsInQueryObject({
        excludedAttributes: ['logo']
      })
    ).subscribe({
      next: (Company) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        Object.assign(this, { Company });
        this.companyLoaded.emit(Company);
        this._cdr.markForCheck();
      }
    });
  }
}
